import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { platforms } from "@/mocks/platforms";

const principles = [
  { icon: "ri-verified-badge-line", title: "只收录正版授权", desc: "坚持只收录公开标注授权关系的官方平台，不收录任何盗版渠道。" },
  { icon: "ri-open-source-line", title: "信息真实可查", desc: "所有入口与说明均可在官方平台核实，拒绝虚构与夸大宣传。" },
  { icon: "ri-heart-2-line", title: "尊重创作", desc: "倡导正版阅读，支持创作者与漫画行业健康、可持续发展。" },
  { icon: "ri-refresh-line", title: "持续更新", desc: "持续跟进官方平台动态，及时更新正版韩漫入口与内容信息。" },
];

const feedback = [
  { quote: "以前总怕点到盗版站，现在从正版韩漫大全进入，追更省心多了。", name: "来自读者的反馈", role: "韩漫爱好者" },
  { quote: "分类很清晰，想看的题材一眼就能找到官方入口，体验很顺。", name: "来自读者的反馈", role: "追更用户" },
  { quote: "认准正版是对作者的尊重，很感谢这个站帮我们避开盗版坑。", name: "来自读者的反馈", role: "漫画读者" },
];

export default function AboutPage() {
  usePageMeta("关于我们 - 正版韩漫大全", "了解正版韩漫大全的品牌介绍、网站定位、内容原则与联系方式，认识一个专注正版韩漫渠道聚合的导航网站。");

  return (
    <div className="bg-background-50">
      <div className="bg-primary-50/50">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav className="text-xs text-foreground-500" aria-label="面包屑">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-800" aria-current="page">关于我们</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">关于正版韩漫大全</h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">一个专注正版韩漫渠道聚合与资讯整理的导航网站。</p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">品牌介绍</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-900">从「想看清正版入口」出发</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              面对鱼龙混杂的韩漫网站，很多读者不知道该从哪里进入官方正版渠道。正版韩漫大全正是为解决这个问题而生——
              我们集中整理官方入口、正版授权信息与独家内容，帮助读者快速、安全地找到想看的韩漫。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们相信，一部作品的价值值得被尊重。通过正版韩漫大全，我们希望让「看正版」这件事变得更简单，也让创作生态走得更远。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/#official"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看官方入口 <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                联系我们
              </Link>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20Korean%20webtoon%20illustration%20of%20a%20group%20of%20friendly%20people%20reading%20comics%20together%20in%20a%20warm%20cozy%20room%2C%20cream%20and%20coral%20palette%2C%20friendly%20community%20atmosphere%2C%20clean%20manga%20lineart%2C%20high%20detail%20digital%20art&width=1000&height=800&seq=about-2026&orientation=portrait"
              alt="正版韩漫大全品牌介绍插画"
              className="aspect-[5/4] w-full rounded-lg object-cover object-top"
            />
            <div className="absolute -bottom-4 -left-4 hidden rounded-lg bg-secondary-500 px-5 py-4 text-background-50 md:block">
              <p className="font-comic text-2xl">100%</p>
              <p className="text-xs">正版授权导向</p>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <div className="text-center">
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">网站定位</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">我们坚持的内容原则</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-5 text-center">
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`text-xl ${p.icon}`}></i>
                </span>
                <h3 className="mt-4 text-sm font-semibold text-foreground-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <div className="text-center">
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">读者口碑</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">来自读者的声音</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {feedback.map((f) => (
              <figure key={f.name} className="rounded-lg border border-background-200 bg-background-100 p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-300"></i>
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">{f.quote}</blockquote>
                <figcaption className="mt-4 flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 text-xs text-background-50">
                    <i className="ri-user-3-line"></i>
                  </span>
                  <div>
                    <p className="text-xs font-medium text-foreground-900">{f.name}</p>
                    <p className="text-xs text-foreground-500">{f.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <div className="mt-16 overflow-hidden rounded-lg bg-foreground-950 p-8 md:p-12">
          <div className="flex flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
            <div>
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">加入我们，一起支持正版韩漫</h2>
              <p className="mt-2 max-w-xl text-sm text-background-100/80">
                有任何建议或合作意向，欢迎与我们联系。你的每一条反馈，都在帮助我们做得更好。
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              联系我们 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-heading text-lg font-bold text-foreground-900 md:text-xl">收录的官方平台</h2>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {platforms.map((p) => (
              <div key={p.id} className="flex flex-col items-center rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                <span className={`flex h-10 w-10 items-center justify-center rounded-md ${p.color} text-background-50`}>
                  <i className={`text-lg ${p.icon}`}></i>
                </span>
                <p className="mt-2 text-xs font-medium text-foreground-900">{p.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}